import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { TranslateModule } from '@ngx-translate/core';
import { DialogRef } from '@angular/cdk/dialog';
import { PerioExamRecording } from '@ffd/api-client';
import { PerioExamRecordingsModalService } from './perio-exam-recordings-modal.service';

@Component({
  selector: 'lib-perio-exam-recordings-modal',
  imports: [CommonModule, SvgIconComponent, TranslateModule],
  templateUrl: './perio-exam-recordings-modal.component.html',
  styleUrl: './perio-exam-recordings-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PerioExamRecordingsModalService],
})
export class PerioExamRecordingsModalComponent implements OnInit {
  private readonly dialogRef = inject(DialogRef);
  protected readonly service = inject(PerioExamRecordingsModalService);

  protected readonly recordings = this.service.recordingsSignal;
  protected readonly isLoading = this.service.isLoadingSignal;
  protected readonly errorMessage = this.service.errorMessageSignal;
  protected readonly showLogsOverlay = signal<{ recordingId: string; logText: string } | null>(null);

  public ngOnInit(): void {
    this.service.init();
  }

  protected onClose(): void {
    this.dialogRef.close();
  }

  protected onShowLogs(recordingId: string): void {
    const recording = this.recordings()?.find((r) => r.recordingId === recordingId);
    if (recording?.logText) {
      this.showLogsOverlay.set({ recordingId, logText: recording.logText });
    }
  }

  protected onCloseLogsOverlay(): void {
    this.showLogsOverlay.set(null);
  }

  protected onDownload(recording: PerioExamRecording): void {
    if (recording.audioUrl) {
      const link = document.createElement('a');
      link.href = recording.audioUrl;
      link.download = `recording-${this.formatDate(recording.uploadDate)}.wav`;
      link.target = '_blank';
      document.body.append(link);
      link.click();
      link.remove();
    }
  }

  protected onDelete(recordingId: string): void {
    this.service.deleteRecording(recordingId);
  }

  protected formatDate(dateString: string): string {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }
}
